import { useRetireEndedTables } from "@/hooks/useRetireEndedTables";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Clock3, UsersRound, MoreVertical, Menu as MenuIcon, Search, Receipt, CreditCard, Printer, Wallet, Eye } from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePOS } from "@/context/POSContext";
import { useCaptainAllTablesState } from "@/hooks/useCaptainOperationalState";
import { useActiveOrders } from "@/hooks/useActiveOrders";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { tableTone, toneLabel } from "@/lib/occupancy";
import { elapsedFromSeconds, inr, MONEY_LINE } from "@/lib/format";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import { meshCustomerPath, meshMenuPath, meshBillPath, meshKotsPath, getMeshBase } from "@/lib/captainMesh/routes";
import { CaptainCancelDialog } from "@/components/captain/CaptainCancelDialog";
import * as api from "@/lib/orderApi";
import { byBoardOrder, tileSpan } from "@/lib/tableOrder";
import CheckItemsPreview from "@/components/CheckItemsPreview";
import { TableOptionsSheet } from "@/components/captain/TableOptionsSheet";
import { useCaptainNetwork } from "@/hooks/useCaptainNetwork";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TableActionModals } from "@/components/captain/TableActionModals";
import { Modal } from "@/components/Modals";
import { listPaymentModes } from "@/lib/orderHistoryApi";
import { billCardAmount, billCardNumber, billedOrders, liveMeshOrder, pendingBillCards, tableCardOrder, unsentWarning } from "@/pages/billing/pendingBillCards";
import {
  printDuplicateBill, generateBill, closeOrderOnServer, getOrder,
} from "@/lib/orderApi";

const LIVE_STATE_LABEL = { open: "punching", kot_sent: "in kitchen", billing: "bill printed" };
const stateLabel = (running, tone) => LIVE_STATE_LABEL[running?.status] || toneLabel(tone);
const orNoTotal = value => (value === null || value === undefined || value === "" ? "—" : inr(value));

export default function BillingTables() {
  const { tables, areas, areasHydrated, areaTables, orderBusy, token, subscribeRealtime, settings } = usePOS();
    const { orderForTable: meshOrderForTable } = useCaptainAllTablesState();
    const { orders: restOrders, loaded: restLoaded } = useActiveOrders(token, subscribeRealtime);
    // Tables another device ended stop showing here (2026-10-24).
    useRetireEndedTables(restOrders, restLoaded, token);
    
    // The table's current party only; a printed bill has its own card (pendingBillCards).
    const orderForTable = (tableId) => tableCardOrder({
        meshOrder: meshOrderForTable(tableId),
        restOrders,
        table: tables?.find(t => t.id === tableId),
        serverLive: isOnline && restLoaded,
    });
  const navigate = useNavigate();
  // Supplied by CaptainLayout's <Outlet context>; the hamburger lives in this
  // screen's header but the drawer belongs to the layout.
  const outletContext = useOutletContext() || {};
  const { openDrawer, toggleDesktopSidebar } = outletContext;

  const [search, setSearch] = useState("");
  const [moveMode, setMoveMode] = useState(false);
  const [actionTable, setActionTable] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  const { isOnline } = useCaptainNetwork();
  // Printed bills come from the same live feed as the table cards: a bill leaves
  // its table and appears on its own card in the same refresh (a 15 s poll left
  // a just-printed bill on no card at all).
  const pendingBills = useMemo(() => billedOrders(restOrders), [restOrders]);


  // We group and filter at the same time
  const { groups: areaGroups, tileCount } = useMemo(() => {
    const floor = tables || [];
    const searchLower = search.toLowerCase();
    
    // Filter floor first based on search
    const filteredFloor = floor.filter(t => 
      t.name?.toLowerCase().includes(searchLower) || 
      t.number?.toString().includes(searchLower)
    );

    const grouped = [];
    const placed = new Set();

    (areas || []).forEach(area => {
      const zoneTables = (areaTables || []).filter(at => at.zone === area.id);
      const matchedTables = zoneTables
        .map(at => filteredFloor.find(t => t.number === at.number))
        .filter(Boolean)
        .filter(t => !placed.has(t.id));
      matchedTables.forEach(t => placed.add(t.id));
      // Saved position (Settings → Tables), then number, numerically.
      matchedTables.sort(byBoardOrder);
      
        const expandedTables = [];
        matchedTables.forEach(t => {
          expandedTables.push(t);
          if (getMeshBase() === '/billing') {
            expandedTables.push(...pendingBillCards(t, pendingBills));
          }
        });
        if (expandedTables.length) grouped.push({ ...area, tables: expandedTables });
    });

    const unassigned = filteredFloor
      .filter(t => !placed.has(t.id))
      .sort(byBoardOrder);
    if (unassigned.length) {
      const expandedUnassigned = [];
        unassigned.forEach(t => {
          expandedUnassigned.push(t);
          if (getMeshBase() === '/billing') {
            expandedUnassigned.push(...pendingBillCards(t, pendingBills));
          }
        });
        grouped.push({ id: "__unassigned__", name: "Unassigned", tables: expandedUnassigned });
    }

    const count = grouped.reduce((n, g) => n + g.tables.length, 0);
    if (!count && filteredFloor.length) {
      const all = [...filteredFloor].sort(byBoardOrder);
      const expandedAll = [];
        all.forEach(t => {
          expandedAll.push(t);
          if (getMeshBase() === '/billing') {
            expandedAll.push(...pendingBillCards(t, pendingBills));
          }
        });
        return { groups: [{ id: "__all__", name: null, tables: expandedAll }], tileCount: expandedAll.length };
    }
    return { groups: grouped, tileCount: count };
  }, [areas, areaTables, tables, search, pendingBills]);


  // §39.2 abandonment: a draft that reaches this board with nothing on it — no
  // items, no KOT ever requested — is an order that came to nothing, and it
  // must not hold its table "punching" for the rest of service. The board is
  // the commit point on purpose: while the captain is still on the menu or
  // customer screens the empty state is mid-edit, not abandoned.
  useEffect(() => {
    if (!token) return;
    let stale = false;
    (async () => {
      const { sweepAbandonedDrafts } = await import("@/lib/captainMesh/db");
      if (stale) return;
      await sweepAbandonedDrafts({
        isOnline,
        fetchOrder: id => api.getOrder(id, token),
        fetchItems: id => api.getOrderItems(id, token),
        cancelOnServer: (id, version) =>
          api.cancelOrder(
            id,
            { reason: "Abandoned: nothing was punched" },
            version,
            token
          ),
      }).catch(() => {});
    })();
    return () => { stale = true; };
  }, [token, isOnline]);

  useEffect(() => {
    if (!token || !isOnline) return;
    let stale = false;
    (async () => {
      try {
        const { saveOrderMapping } = await import("@/lib/captainMesh/db");
        const { offlineDb } = await import("@/db/offlineDb");
        const mappings = await offlineDb.mesh_order_map.toArray();
        const LIVE = new Set(["open", "kot_sent", "billing"]);
        const liveMappings = mappings.filter(m => !m.order_status || LIVE.has(m.order_status));
        if (!liveMappings.length || stale) return;

        const board = await api.listActiveOrders(token);
        const activeIds = new Set(board.map(o => o.id));

        for (const m of liveMappings) {
          if (stale) return;
          if (!m.order_id) continue;
          if (activeIds.has(m.order_id)) {
            const serverOrder = board.find(o => o.id === m.order_id);
            if (serverOrder && serverOrder.table_id) {
              const mat = await offlineDb.captain_materialized.get(m.aggregate_id);
              if (mat && mat.state && mat.state.table_id !== serverOrder.table_id) {
                await offlineDb.captain_materialized.put({
                  ...mat,
                  state: {
                    ...mat.state,
                    table_id: serverOrder.table_id,
                  },
                });
              }
            }
            continue;
          }
          try {
            const order = await api.getOrder(m.order_id, token);
            await saveOrderMapping(m.aggregate_id, {
              order_id: m.order_id,
              order_status: order.status,
            });
          } catch {
            await saveOrderMapping(m.aggregate_id, {
              order_id: m.order_id,
              order_status: "closed",
            });
          }
        }
      } catch {}
    })();
    return () => { stale = true; };
  }, [token, isOnline]);

  const [optionsFor, setOptionsFor] = useState(null);
  const holdTimer = useRef(null);
  const heldRef = useRef(false);

  const handleTargetSelection = async (targetTable) => {
    const sourceOrder = orderForTable(actionTable.id);
    const targetOrder = orderForTable(targetTable.id);
    
    if (targetTable.id === actionTable.id) {
      toast.error("Target table cannot be the same as the source table.");
      return;
    }

    const sourceServerId = sourceOrder?.server_order_id || sourceOrder?.id;
    const targetServerId = targetOrder?.server_order_id || targetOrder?.id;

    if (!sourceServerId) {
      toast.error("Source order is not synced to server yet.");
      return;
    }

    try {
      // Re-read the versions from the server every time. The board row can be
      // stale, and on the pad it is the device's own copy with no server version
      // at all — the move went out with no If-Match and the server answered 428
      // "If-Match header required" (C05, order 765, 2026-10-13 17:13 IST).
      const sourceVersion = (await api.getOrder(sourceServerId, token)).version;
      if (actionType === 'select_target_shift') {
        if (targetServerId) {
          const targetVersion = (await api.getOrder(targetServerId, token)).version;
          await api.mergeOrders(targetServerId, sourceServerId, targetVersion, token, sourceVersion);
          const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
          const { offlineDb } = await import("@/db/offlineDb");
          if (sourceOrder?.aggregate_id) {
            await saveOrderMapping(sourceOrder.aggregate_id, { order_id: sourceServerId, order_status: "closed" });
            if (markAggregateDiscarded) await markAggregateDiscarded(sourceOrder.aggregate_id);
          }
          const mat = await offlineDb.captain_materialized.where("state.table_id").equals(actionTable.id).first();
          if (mat) {
            await offlineDb.captain_materialized.delete(mat.aggregate_id);
          }
          toast.success(`Merged ${actionTable.name} into ${targetTable.name}`);
        } else {
          await api.transferOrder(sourceServerId, targetTable.id, sourceVersion, token);
          const { offlineDb } = await import("@/db/offlineDb");
          const mats = await offlineDb.captain_materialized.toArray();
          const sourceMat = mats.find(m => m.aggregate_id === sourceOrder?.aggregate_id || m.state?.table_id === actionTable.id);
          if (sourceMat) {
            await offlineDb.captain_materialized.put({
              ...sourceMat,
              state: {
                ...sourceMat.state,
                table_id: targetTable.id,
              },
            });
          }
          toast.success(`Moved ${actionTable.name} to ${targetTable.name}`);
        }
      } else if (actionType === 'select_target_items') {
        if (!selectedItemIds || selectedItemIds.size === 0) {
          toast.error("Please select at least one item to move.");
          return;
        }
        await api.transferItems(sourceServerId, Array.from(selectedItemIds), targetTable.id, sourceVersion, token);
        const { offlineDb } = await import("@/db/offlineDb");
        const mats = await offlineDb.captain_materialized.toArray();
        const sourceMat = mats.find(m => m.aggregate_id === sourceOrder?.aggregate_id || m.state?.table_id === actionTable.id);
        if (sourceMat && sourceMat.state?.items) {
          const newItems = { ...sourceMat.state.items };
          for (const iid of selectedItemIds) {
            delete newItems[iid];
          }
          await offlineDb.captain_materialized.put({
            ...sourceMat,
            state: {
              ...sourceMat.state,
              items: newItems,
            },
          });
        }
        toast.success(`Moved items to ${targetTable.name}`);
      }
      
      setActionType(null);
      setMoveMode(false);
      setSelectedItemIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Action failed");
    }
  };

  const handleClick = async (table) => {
    const running = liveMeshOrder(meshOrderForTable(table.id), restOrders);

    if (actionType === 'select_target_shift' || actionType === 'select_target_items') {
      await handleTargetSelection(table);
      return;
    }

    if (moveMode) {
      if (!running) {
        toast.error("Cannot move. This table has no active order yet.");
        return;
      }
      if (running.status === "billing" || running.status === "settled") {
        toast.error("Cannot move items from a billed or settled order.");
        return;
      }
      setActionTable(table);
      setActionType("select_action");
      return;
    }

    // Navigation Action ONLY. Do not create an order.
    
    // Check if the table has a pending bill (since tables are released on print,
    // the bill might be floating waiting for settlement)
    const pending = pendingBills.find(b => b.table_id == table.id || b.table_number == table.number);
    
    if (table.isPendingBillCard) {
      navigate(`/billing/table/${table.pendingBill.table_id || table.id}?order=${table.pendingBill.id}`);
      return;
    }

    const restOrder = restOrders.find(row => row.table_number === table.number && row.status !== "billing");
      const serverOccupied = !!restOrder;
    if (running) {
      // Locally-known order: straight to the catalogue.
      navigate(`/billing/table/${table.id}`);
    } else if (serverOccupied) {
      // The server holds an order this device has no aggregate for (opened on
      // the till, or before this device's IndexedDB existed). Single tap must
      // enter the order-editing workflow like any other occupied table - so
      // ADOPT the server order into local state (acked events, nothing sent)
      // and open the catalogue on it. Options belong to long-press only.
      const adopted = await adoptIfNeeded(table);
      if (adopted) navigate(`/billing/table/${table.id}`);
    } else {
      // Empty table, no need to collect customer first in Billing
      navigate(`/billing/table/${table.id}`);
    }
  };

  // Teach this device about a server-side order so any order workflow (menu,
  // edit-customer, summary) can run on it. True when an aggregate exists after
  // the call; false means the caller should not navigate.
  const adoptIfNeeded = async table => {
    if (orderForTable(table.id)) return true;
    if (!isOnline || !token) {
      toast.error(`${table.name} is served from another terminal — reconnect to edit it here`);
      return false;
    }
    try {
      const board = await api.listActiveOrders(token);
      const row = board.find(o => o.status !== "billing" && ((table.id != null && o.table_id === table.id) || o.table_number === table.number));
      if (!row) {
        toast.error(`${table.name} has no running order on the server`);
        return false;
      }
      const [order, items] = await Promise.all([
        api.getOrder(row.id, token),
        api.getOrderItems(row.id, token),
      ]);
      let customer = null;
      if (order.customer_id) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/customers/${order.customer_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.ok) customer = await res.json();
        } catch { customer = null; }
      }
      const { adoptServerOrder } = await import("@/lib/captainMesh/db");
      const { uuid } = await import("@/lib/uuid");
      await adoptServerOrder({ order, items, customer, tableDbId: table.id, deviceId: null, makeId: uuid });
      return true;
    } catch {
      toast.error(`Could not open ${table.name} — try again`);
      return false;
    }
  };

  // Long-press -> options. Pointer events cover touch and mouse alike; the tap
  // handler checks heldRef so releasing after a hold does not ALSO navigate.
  const holdStart = (table) => {
    heldRef.current = false;
    holdTimer.current = setTimeout(() => {
      heldRef.current = true;
      const running = liveMeshOrder(meshOrderForTable(table.id), restOrders);
      const restOrder = restOrders.find(row => row.table_number === table.number && row.status !== "billing");
      const serverOccupied = !!restOrder;
      if (running || serverOccupied) setOptionsFor(table);
    }, 500);
  };
  const holdEnd = () => clearTimeout(holdTimer.current);

  const optionsRunning = optionsFor ? orderForTable(optionsFor.id) : null;
  // A local claim the server does not share (offline, or the server says the
  // table is free) is the stale-lease case - automatically reclaimed.
  const optionsStale =
    optionsRunning &&
    // A mapped aggregate IS a real server order - never a stale lease, whatever
    // the (possibly stale) cached table row says about the table.
    !optionsRunning.server_order_id &&
    (!isOnline || !["occupied", "billing"].includes(optionsFor?.status));

  // Auto-clear stale claim when detected - no UI confirm needed
  if (optionsStale) {
    (async () => {
      const agg = optionsRunning?.order_id;
      const tableName = optionsFor?.name;
      if (!agg) return;
      const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
      await markAggregateRejected(agg);
      if (markAggregateDiscarded) await markAggregateDiscarded(agg);
      toast.success(`${tableName} is available again`);
      setOptionsFor(null);
    })();
  }

  const reclaim = async () => {
    const agg = optionsRunning?.order_id;
    const tableName = optionsFor?.name;
    setOptionsFor(null);
    if (!agg) return;
    const lines = Object.values(optionsRunning.items || {}).filter(
      i => i.status !== "cancelled" && i.status !== "removed"
    );
    const ok = window.confirm(
      `Clear this device's claim on ${tableName}?` +
        (lines.length ? ` ${lines.length} unsent item(s) on it will be discarded.` : "")
    );
    if (!ok) return;
    const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
    await markAggregateRejected(agg);
    if (markAggregateDiscarded) await markAggregateDiscarded(agg);
    console.warn("[CaptainPAD] table claim reclaimed", { table: tableName, aggregate: agg, discarded_lines: lines.length });
    toast.success(`${tableName} is available again`);
  };

  // Cancel Table: gated behind the manager employee-ID + PIN inside
  // CancelOrderDialog, which re-verifies via POST /auth/authorize server-side -
  // "password protection" through the audited path, not a client-side check.
  const [cancelFor, setCancelFor] = useState(null);

  // ── Billing-board quick actions (the 4 buttons under each table tile) ──
  // Each fires the SAME backend call the full table/Bill screen uses, so the board
  // is a fast lane, not a parallel implementation. Settlement needs a payment-mode
  // pick; KOT print needs a KOT pick (an order can carry several).
  const [settleFor, setSettleFor] = useState(null);   // { id, version, order_number, name }
  const [quickBusy, setQuickBusy] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    listPaymentModes(token)
      .then(rows => { if (alive) setPaymentModes((rows || []).filter(m => m.is_active !== false)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);

  // Resolve the server order id + current version for a table tile, from either
  // the mesh aggregate or the live board row.
  const resolveOrderRef = async (table, running) => {
    const serverId = running?.server_order_id || running?.id;
    if (serverId) {
      const fresh = await getOrder(serverId, token);
      return { id: serverId, version: fresh.version, order_number: fresh.order_number };
    }
    const board = await api.listActiveOrders(token);
    const row = board.find(o => o.status !== "billing" && ((table.id != null && o.table_id === table.id) || o.table_number === table.number));
    if (!row) return null;
    return { id: row.id, version: row.version, order_number: row.order_number };
  };

  const quickPrintBill = async (table, running) => {
    // A second tap during the first must not POST /bill again on the same version.
    if (quickBusy) return;
    const ref = await resolveOrderRef(table, running);
    if (!ref) { toast.error(`${table.name} has no server order to bill yet`); return; }
    setQuickBusy(true);
    try {
      await generateBill(ref.id, {}, ref.version, token);
      toast.success(`Bill printed${table.name ? ` for ${table.name}` : ""} — table released, bill in Pending Bills`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Print failed");
    } finally { setQuickBusy(false); }
  };

  const quickPrintDuplicate = async (table, running) => {
    if (quickBusy) return;
    const ref = await resolveOrderRef(table, running);
    if (!ref) { toast.error(`${table.name} has no bill to reprint`); return; }
    setQuickBusy(true);
    try {
      await printDuplicateBill(ref.id, token);
      toast.success(`Duplicate bill printing for #${ref.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Duplicate print failed");
    } finally { setQuickBusy(false); }
  };

  // Check KOT = the table's "Check Items" slip at its bill printer (owner,
  // 2026-10-24). It reprinted the kitchen ticket, so a check went to the kitchen
  // as a KOT.
  // Opens the slip on screen first (owner, 2026-10-25); its Print button prints.
  const [checkPreview, setCheckPreview] = useState(null);
  const quickPrintCheck = async (table, running) => {
    const ref = await resolveOrderRef(table, running);
    if (!ref) { toast.error(`${table.name} has no server order yet`); return; }
    setCheckPreview({ orderId: ref.id, tableName: table.name });
  };

  const quickSettle = async (table, running) => {
    const ref = await resolveOrderRef(table, running);
    if (!ref) { toast.error(`${table.name} has no server order to settle`); return; }
    if (!paymentModes.length) {
      toast.error("No payment modes configured");
      return;
    }
    setSettleFor({ ...ref, name: table.name });
  };

  const doSettle = async (mode) => {
    if (!settleFor || quickBusy) return;
    setQuickBusy(true);
    try {
      const fresh = await getOrder(settleFor.id, token);
      await closeOrderOnServer(settleFor.id, mode.name, fresh.version, token);
      toast.success(`#${settleFor.order_number} settled · ${mode.display_label || mode.name}`);
      setSettleFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Settlement failed");
    } finally { setQuickBusy(false); }
  };

  const openCancel = async table => {
    setOptionsFor(null);
    const running = liveMeshOrder(meshOrderForTable(table.id), restOrders);
    let serverId = running?.server_order_id ?? null;
    let orderNumber = running?.order_number ?? null;
    if (!serverId && token) {
      try {
        const board = await api.listActiveOrders(token);
        const row = board.find(o => o.status !== "billing" && ((table.id != null && o.table_id === table.id) || o.table_number === table.number));
        serverId = row?.id ?? null;
        // The REAL order number rides with the id - "Cancel order #undefined"
        // was this lookup not happening.
        orderNumber = row?.order_number ?? null;
      } catch { serverId = null; }
    }
    if (!serverId) {
      if (running) {
        const ok = window.confirm(
          `There is no active order on the server for ${table.name}. Discard this device's local draft and free the table?`
        );
        if (!ok) return;
        try {
          const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
          if (running.order_id) {
            await markAggregateRejected(running.order_id);
            if (markAggregateDiscarded) await markAggregateDiscarded(running.order_id);
          }
          toast.success(`${table.name} is available again`);
        } catch (err) {
          toast.error(`Could not free ${table.name}: ${err?.message || err}`);
        }
        return;
      }
      const ok = window.confirm(
        `${table.name} has no running order on the server. Release and mark it available?`
      );
      if (ok) {
        try {
          await api.releaseTable(table.id, token);
          toast.success(`${table.name} is now available`);
        } catch (err) {
          toast.error(`Could not release ${table.name}: ${err?.response?.data?.detail || err?.message || err}`);
        }
      }
      return;
    }
    setCancelFor({ table, serverId, running, orderNumber });
  };

  const confirmCancelTable = async ({ reasonCode, reason, authorizedBy }) => {
    const { table, serverId, running } = cancelFor;
    try {
      const fresh = await api.getOrder(serverId, token);
      let result = { void_slips: 0 };
      if (fresh.status !== "cancelled" && fresh.status !== "closed") {
        result = await api.cancelOrder(serverId, { reasonCode, reason, authorizedBy }, fresh.version, token);
      }
      if (running) {
        const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
        await saveOrderMapping(running.order_id, { order_id: serverId, order_status: "cancelled" });
        if (markAggregateDiscarded) await markAggregateDiscarded(running.order_id);
      }
      setCancelFor(null);
      toast.success(
        `${table.name} cancelled` + (result.void_slips ? ` · ${result.void_slips} VOID slip(s) sent` : "")
      );
      return "done";
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && (detail.toLowerCase().includes("cancelled") || detail.toLowerCase().includes("transition"))) {
        if (running) {
          const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
          await saveOrderMapping(running.order_id, { order_id: serverId, order_status: "cancelled" });
          if (markAggregateDiscarded) await markAggregateDiscarded(running.order_id);
        }
        setCancelFor(null);
        toast.success(`${table.name} cancelled`);
        return "done";
      }
      throw e;
    }
  };

  // The testid stays mesh-specific: CaptainPage carried "captain-table-screen"
  // too, so an e2e wait on that matched whichever page happened to be mounted.
  return (
    <div className="flex flex-col h-full bg-background relative" data-testid="captain-mesh-table-screen">
      
      {/* HEADER */}
      {/* DESIGN.md §4 "One shell": the same white header as every other page, no solid-colour bar. */}
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-4 bg-card text-foreground border-b sticky top-0 z-20 shrink-0 gap-3">
        

        {/* Sidebar / Drawer menu button */}
        <button
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
          onClick={() => {
            if (toggleDesktopSidebar) toggleDesktopSidebar();
            else window.dispatchEvent(new Event('toggle-sidebar'));
          }}
          aria-label="Toggle menu"
          data-testid="billing-tables-menu-button"
        >
          <MenuIcon className="w-5 h-5" />
        </button>
        <h1 className="text-title font-semibold hidden sm:block shrink-0">Tables</h1>
        
        <div className="flex-1 min-w-[7rem] max-w-sm relative flex items-center">
          <Search className="w-4 h-4 absolute left-3 text-muted-foreground" />
          <input
            type="text"
            className="w-full h-9 pl-9 pr-3 rounded-md bg-background border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
            placeholder="Search tables..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-snug sm:gap-3 shrink-0">
          <div className="flex items-center sm:mr-2">
            {moveMode ? (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => {
                  setMoveMode(false);
                  setActionType(null);
                  setActionTable(null);
                  setSelectedItemIds(new Set());
                }}
                className="font-medium h-11 px-3 sm:px-4"
              >
                {/* One text run: the Button's flex gap split "Cancel" and " Move" into
                    two items and showed a double space. 44px: the bill desk is touch. */}
                <span>Cancel<span className="hidden sm:inline"> Move</span></span>
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setMoveMode(true)}
                className="font-medium h-11 px-3 sm:px-4"
              >
                <span>Move<span className="hidden sm:inline" data-testid="move-label-long"> KOT/Items</span></span>
              </Button>
            )}
          </div>
          <NetworkIndicator plain />
        </div>
      </header>

      {/* GRID */}
      {actionType === 'select_target_shift' || actionType === 'select_target_items' ? (
        <div className="bg-primary/10 text-primary px-base py-3 flex justify-between items-center shrink-0 border-b border-primary/20">
          <div className="flex flex-col">
            <strong className="text-body font-semibold">Select destination for {actionTable?.name}</strong>
            <small className="text-meta opacity-80">Click a table below to complete the transfer</small>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setActionType(null)}>Cancel</Button>
        </div>
        ) : moveMode ? (
          <div className="px-base pt-snug text-meta font-medium text-muted-foreground shrink-0">
            Select a running table to shift or merge
          </div>
        ) : null}
      <div className="flex-1 overflow-y-auto p-base space-y-loose pb-section">
        {areaGroups.map(area => (
          <div key={area.id} className="space-y-3">
            {area.name && <h2 className="text-meta font-medium text-muted-foreground">{area.name}</h2>}
            {/* Columns follow the space the grid actually has (DESIGN.md §3.1, V3), not
                the viewport: with the sidebar open an 820px tablet left ~86px tiles and
                the running-table content overlapped. A tile is never narrower than 8rem. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] auto-rows-[minmax(8.5rem,auto)] grid-flow-dense gap-2" data-testid="table-grid">
              {area.tables.map((table, i) => {
                 let tone = "vacant";
                   let running = null;
                   if (table.isPendingBillCard) {
                       tone = "billing";
                   } else {
                       running = orderForTable(table.id);
                       // Connected and the board read: the live orders are the truth. The
                       // table's cached status is from this device's last bootstrap and never
                       // refreshed, so a table freed since stayed blue (owner, 2026-10-26).
                       tone = tableTone(running, isOnline && restLoaded ? null : table);
                   }


                 // DESIGN.md §4.1: a vacant table is the resting state — a plain 1px
                 // border, no colour, no "VACANT" word on forty tiles. Only tables that
                 // need attention are tinted (semantic tokens, legible in dark mode
                 // because the text stays `foreground`).
                 let colorClasses = "border-border bg-card text-foreground";
                 if (tone === "occupied") colorClasses = "border-info/50 bg-info-subtle text-foreground";
                 if (tone === "billing") colorClasses = "border-warning/50 bg-warning-subtle text-foreground";
                 if (tone === "conflicted") colorClasses = "border-danger/60 bg-danger-subtle text-foreground";
               let disabledOpacity = ""; let isDisabled = false;
                 const isTargetSelection = actionType === 'select_target_shift' || actionType === 'select_target_items';
                 
                 if (isTargetSelection) {
                   if (table.id === actionTable?.id || tone === "billing" || running?.status === "settled") {
                     disabledOpacity = "opacity-50 grayscale cursor-not-allowed"; isDisabled = true;
                   }
                 } else if (moveMode) {
                   if (tone === "vacant" || tone === "billing" || running?.status === "settled") {
                     disabledOpacity = "opacity-50 grayscale cursor-not-allowed"; isDisabled = true;
                   }
                 }

                 // Layout in normal flow (it used to be aspect-square with absolute
                 // overlays, and on a narrow tile the timer sat on the name and the
                 // buttons spilled out). The card is the visual box; the open-the-table
                 // target and the quick actions are SIBLINGS inside it, so an action is
                 // never a button nested inside a button, and a tap on an action can
                 // never also open the table.
                 const openTable = () => { if (!orderBusy && !isDisabled) handleClick(table); };
                 return (
                   <div key={table.id} className="relative animate-stagger-pop" style={{ animationDelay: `${i * 30}ms`, ...tileSpan(table) }}>
                     <div className={`h-full min-h-[8.5rem] flex flex-col rounded-lg border ${colorClasses} ${disabledOpacity}`}>
                     <div
                          role="button"
                          tabIndex={isDisabled ? -1 : 0}
                          aria-disabled={isDisabled || undefined}
                          className={`flex-1 flex flex-col items-center px-2 pt-1.5 pb-2 rounded-lg select-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25 ${isDisabled ? "cursor-not-allowed" : "cursor-pointer transition-transform active:scale-95"}`}
                          onClick={openTable}
                          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTable(); } }}
                          onContextMenu={e => e.preventDefault()}
                          data-tone={tone}
                          data-testid={table.isPendingBillCard ? `bill-card-${table.pendingBill.id}-button` : `table-${table.name}-button`}
                       >
                         <div className="w-full flex justify-between items-start text-meta font-medium text-muted-foreground">
                           <div className="flex items-center gap-1.5">
                             {running && (
                               <span className="flex items-center gap-0.5 tabular-nums tracking-tighter">
                                 <Clock3 className="w-3.5 h-3.5"/>{elapsedFromSeconds(running.elapsed_seconds)}
                               </span>
                             )}
                           </div>
                           <span className="flex items-center gap-1.5 tabular-nums">
                             <span className="flex items-center gap-0.5">
                               <UsersRound className="w-3.5 h-3.5"/>{running ? running.guest_count : table.isPendingBillCard ? table.pendingBill.guest_count : table.covers}
                             </span>
                             {table.isPendingBillCard && billCardNumber(table.pendingBill) && (
                               <span className="font-semibold text-foreground" data-testid={`bill-card-${table.pendingBill.id}-number`}>
                                 {billCardNumber(table.pendingBill)}
                               </span>
                             )}
                           </span>
                         </div>
                         
                         <div className="flex-1 flex flex-col items-center justify-center gap-0.5 pt-1 text-center">
                           <strong className="text-lede font-semibold leading-tight w-min mx-auto px-1">{table.name}</strong>
                           {running && orNoTotal(running.amount) !== "—" && (
                             <span className={`${MONEY_LINE} leading-tight`}>
                               {orNoTotal(running.amount)}
                             </span>
                           )}
                           {table.isPendingBillCard && orNoTotal(billCardAmount(table.pendingBill)) !== "—" && (
                             <span className={`${MONEY_LINE} leading-tight`} data-testid={`bill-card-${table.pendingBill.id}-amount`}>
                               {orNoTotal(billCardAmount(table.pendingBill))}
                             </span>
                           )}
                           {unsentWarning(running, settings?.unsent_items_warn_minutes) && (
                             <span className="mt-0.5 px-1.5 rounded border border-danger/40 bg-danger-subtle text-danger text-micro font-bold" data-testid={`table-${table.name}-unsent`}>
                               {unsentWarning(running, settings?.unsent_items_warn_minutes)}
                             </span>
                           )}
                         </div>
                         {/* The tone is carried by the tint and by which actions the card
                             offers; the state word stays available to screen readers. */}
                         <span className="sr-only">{running ? stateLabel(running, tone) : toneLabel(tone)}</span>
                     </div>

                      {tone !== "vacant" && !moveMode && !isTargetSelection && (
                        // Hidden while picking a move source/destination: a Print Bill
                        // tapped there froze the very order being moved.
                        // Quick actions sit at opposite edges, ≥ 48px, in different
                        // styles (DESIGN.md §3.1): the harmless one is quiet on the
                        // left; the one that changes the order (Print Bill freezes it
                        // for billing; Settle closes it) is outlined or green on the right.
                        <div className="flex justify-between items-center gap-2 px-1.5 pb-1.5">
                          {tone === "billing" || (running && running.status === "settled") ? (
                            <>
                              <button
                                type="button"
                                className="w-12 h-12 shrink-0 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                                onClick={() => quickPrintDuplicate(table, table.isPendingBillCard ? table.pendingBill : running)}
                                disabled={quickBusy}
                                aria-label="Print Duplicate Bill"
                                title="Print Duplicate Bill"
                              >
                                <Printer className="w-5 h-5" />
                              </button>
                              <button
                                type="button"
                                className="w-12 h-12 shrink-0 flex items-center justify-center rounded-md bg-action text-action-foreground shadow-sm hover:bg-action/90 transition-colors disabled:opacity-50"
                                onClick={() => quickSettle(table, table.isPendingBillCard ? table.pendingBill : running)}
                                disabled={quickBusy}
                                aria-label="Settle"
                                title="Settle Bill"
                              >
                                <Wallet className="w-5 h-5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="w-12 h-12 shrink-0 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                                onClick={() => quickPrintCheck(table, running)}
                                disabled={quickBusy}
                                aria-label="Print Check KOT"
                                title="Print Check KOT"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                type="button"
                                className="w-12 h-12 shrink-0 flex items-center justify-center rounded-md border border-input bg-card text-foreground shadow-sm hover:bg-muted transition-colors disabled:opacity-50"
                                onClick={() => quickPrintBill(table, running)}
                                disabled={quickBusy}
                                aria-label="Print Bill"
                                title="Print Bill"
                              >
                                <Printer className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                     </div>
                   </div>
                 );
              })}
            </div>
          </div>
        ))}

        {!tileCount && (
          !areasHydrated
            ? <LoadingState message="Reading the floor plan…" testId="captain-tables-loading" />
            : <EmptyState
                title={search ? "No tables found" : "No tables"}
                message={search ? "No tables match your search query." : "No tables have been loaded."}
                testId="captain-tables-empty"
              />
        )}
      </div>

      {checkPreview && (
        <CheckItemsPreview orderId={checkPreview.orderId} tableName={checkPreview.tableName} token={token}
          onClose={() => setCheckPreview(null)} />
      )}
      {optionsFor && (
        <TableOptionsSheet
          table={optionsFor}
          onCheckKot={async () => { const t = optionsFor; const running = optionsRunning; setOptionsFor(null); await quickPrintCheck(t, running); }}
          onViewKots={() => { setOptionsFor(null); navigate(meshKotsPath(optionsFor.id)); }}
          onPrintBill={() => { setOptionsFor(null); navigate(meshBillPath(optionsFor.id)); }}
          onEditCustomer={async () => { const t = optionsFor; setOptionsFor(null); if (await adoptIfNeeded(t)) navigate(`${meshCustomerPath(t.id)}?edit=1`); }}
          onCancelTable={() => openCancel(optionsFor)}
          onReclaim={optionsRunning && !optionsRunning.server_order_id ? reclaim : (optionsStale ? reclaim : null)}
          reclaimHint={optionsRunning && !optionsRunning.server_order_id ? "Discard unsent order and free table" : (optionsStale ? "Release table occupied only on this device" : null)}
          onClose={() => setOptionsFor(null)}
        />
      )}

      {cancelFor && (
        <CaptainCancelDialog
          orderNumber={cancelFor.orderNumber}
          tableName={cancelFor.table?.name}
          onConfirm={confirmCancelTable}
          onClose={() => setCancelFor(null)}
        />
      )}

      {actionType && (
        <TableActionModals 
          actionTable={actionTable}
          actionType={actionType}
          setActionType={setActionType}
          selectedItemIds={selectedItemIds}
          setSelectedItemIds={setSelectedItemIds}
          token={token}
          tables={tables}
          orderForTable={orderForTable}
          onSuccess={() => {
            setActionType(null);
            setMoveMode(false);
          }}
        />
      )}

      {settleFor && (
        <Modal title={`Settle ${settleFor.name || ""} · #${settleFor.order_number}`} onClose={() => setSettleFor(null)} testId="billing-settle-modal">
          <div className="p-4 space-y-2">
            <p className="text-micro font-bold text-muted-foreground mb-1">Choose payment method</p>
            {paymentModes.map(mode => (
              <Button
                key={mode.name}
                className="w-full h-14 text-body justify-start gap-3 rounded-xl"
                variant={mode.is_cash ? "default" : "outline"}
                disabled={quickBusy}
                onClick={() => doSettle(mode)}
                data-testid={`settle-mode-${mode.name}`}
              >
                {mode.display_label || mode.name}
              </Button>
            ))}
            {!paymentModes.length && (
              <p className="text-center text-meta text-muted-foreground py-4">No payment modes available</p>
            )}
          </div>
        </Modal>
      )}

    </div>
  );
}



